import heic2any from 'heic2any';

const HEIC_EXTENSIONS = ['heic', 'heif'];

export const isHeicFile = (filePath) => {
  if (!filePath) return false;
  const ext = filePath.split('.').pop()?.toLowerCase();
  return HEIC_EXTENSIONS.includes(ext);
};

export const isHeicBlob = (blob) => {
  if (!blob) return false;
  const type = blob.type?.toLowerCase() || '';
  return type === 'image/heic' || type === 'image/heif';
};

/**
 * Convert a HEIC/HEIF blob to JPEG. Returns the original blob untouched if
 * it is not HEIC or if conversion fails (graceful degradation).
 */
export const convertHeicBlobToJpeg = async (blob) => {
  try {
    const result = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
    return Array.isArray(result) ? result[0] : result;
  } catch (err) {
    console.warn('HEIC→JPEG conversion failed, returning original blob:', err);
    return blob;
  }
};

/**
 * If `file` is HEIC/HEIF, convert it to a JPEG File object suitable for
 * upload.  Preserves the original name (with .jpg extension).
 * Returns the original File untouched for non-HEIC inputs.
 */
export const normalizeHeicFileForUpload = async (file) => {
  if (!file) return file;

  const ext = file.name?.split('.').pop()?.toLowerCase();
  const isHeic = HEIC_EXTENSIONS.includes(ext) ||
    file.type === 'image/heic' ||
    file.type === 'image/heif';

  if (!isHeic) return file;

  const jpegBlob = await convertHeicBlobToJpeg(file);
  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([jpegBlob], newName, { type: 'image/jpeg' });
};

/**
 * Determine preview-friendly document type from a file path.
 * Returns 'image' | 'pdf' | null.
 */
export const getPreviewDocumentType = (filePath) => {
  if (!filePath) return null;
  const path = filePath.toLowerCase();
  if (path.match(/\.(jpg|jpeg|png|gif|heic|heif|webp)$/)) return 'image';
  if (path.match(/\.pdf$/)) return 'pdf';
  return null;
};

/**
 * Infer a browser-usable MIME type from a file extension.
 * Falls back to the Content-Type header value or application/octet-stream.
 */
export const inferContentType = (fileExt, headerContentType) => {
  const ext = (fileExt || '').toLowerCase();
  const map = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
  };
  return headerContentType || map[ext] || 'application/octet-stream';
};
